<script lang="ts" setup>
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import {
  ActionTypes,
  AsyncStatus,
  GettersTypes,
  MutationTypes,
  type PreAuthAction,
  type State,
} from '@/store/store_types'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed } from 'vue'
import { useStore } from 'vuex'
import ElevatedBlockBtn from '../ui-elements/buttons/ElevatedBlockBtn.vue'
import TextBtn from '../ui-elements/buttons/TextBtn.vue'

const store = useStore<State>()

const stockVehicles = computed<Vehicle[]>(() => store.getters[GettersTypes.stockVehicles])

const isAuthorised = computed(() => store.state.authorizedFlag)

function imageSrc(vehicle: Vehicle): string {
  return (
    vehicle?.imageSrcPaths?.[0] ??
    vehicle?.evModel?.imageSrc ??
    getAssetSrc('car_images/placeholder_car.png')
  )
}

function viewVehicle(vehicle: Vehicle) {
  store.commit(MutationTypes.setViewedFleetVehicle, vehicle.localId)
  store.commit(MutationTypes.setDashboardDialogContent, 'vehicle-details')
  store.commit(MutationTypes.setShowDashboard, true)
}

function viewAllVehicles() {
  store.commit(MutationTypes.setDashboardDialogContent, 'fleet-vehicles')
  store.commit(MutationTypes.setShowDashboard, true)
}

function contactSalesTeamPreCheck() {
  if (isAuthorised.value) {
    store.dispatch(ActionTypes.createSalesLead)
  } else {
    store.commit(MutationTypes.setRetuning_auth_action, 'connect_with_sales' as PreAuthAction)
    store.commit(MutationTypes.setShow_account_needed, true) // show account needed alert
  }
}

const status = computed(() => store.state.creatingSalesLeadStatus)

const isSalesTeam = computed(() =>
  store.state.permissions.hasAccessPermission('sales_leads', 'read'),
)
</script>

<template>
  <v-card flat color="transparent">
    <v-card-title class="d-flex justify-space-between align-center text-pwt_dark_grey px-7">
      <span> Our vehicles </span>
      <TextBtn @click="viewAllVehicles"> Browse all </TextBtn>
    </v-card-title>
    <v-slide-group class="bg-transparent mb-7">
      <v-slide-group-item v-for="(item, index) in stockVehicles" :key="index">
        <v-card
          elevation="4"
          width="150px"
          class="ma-2"
          :class="{ 'ml-7': index === 0, 'mr-7': index === stockVehicles.length - 1 }"
          rounded="lg"
          @click="viewVehicle(item)"
          color="pwt_snow"
        >
          <v-card-text class="pt-0">
            <div class="rounded-lg ma-1 overflow-hidden" style="height: 50%; width: 100%">
              <v-img
                contain
                :src="imageSrc(item)"
                aspect-ratio="1"
                width="100%"
                height="100%"
              ></v-img>
            </div>
            <v-row no-gutters style="font-size: 16px !important">
              {{ item.licensePlate ?? item.name ?? item.evModel?.name ?? 'sale vehicle' }}
            </v-row>
            <v-row no-gutters v-if="item.licensePlate" style="font-size: 12px !important">
              {{ item.evModel?.name ?? 'combustion vehicle' }}
            </v-row>
          </v-card-text>
        </v-card>
      </v-slide-group-item>
    </v-slide-group>
    <template v-if="!isSalesTeam">
      <v-card-actions v-if="status === AsyncStatus.PROCESSING" class="px-7">
        <ElevatedBlockBtn processing> Connect with our sales team </ElevatedBlockBtn>
      </v-card-actions>
      <v-card v-else-if="status === AsyncStatus.FAILED" flat color="transparent" class="px-7 pa-5">
        <v-alert type="error"> Unable to connect with sales. Please try again. </v-alert>
      </v-card>
      <v-card v-else-if="status === AsyncStatus.SUCCESS" flat color="transparent" class="px-7 pa-5">
        <v-alert type="success"> Successfully connected with sales. </v-alert>
      </v-card>
      <v-card-actions v-else class="px-7">
        <ElevatedBlockBtn @click="contactSalesTeamPreCheck">
          Connect with our sales team
        </ElevatedBlockBtn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<style scoped>
:deep(.v-slide-group__prev) {
  background-color: transparent !important;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1101;
}

:deep(.v-slide-group__next) {
  background-color: transparent !important;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}
</style>
